import { Box, Card, Dropdown, H4 } from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { getAvailableTriggerEventsV2 } from "api/services/programService";
import { When } from "components/When/When";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useCheckPermission } from "hooks/usePermissions";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ProgramsToTrigger } from "./ProgramsToTrigger";
import { TriggerByPrograms } from "./TriggerByPrograms";
import { useDisabledFields } from "./useDisabledFields";
import { useTranslation } from "react-i18next";

export const ProgramTriggers = () => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { programId } = useParams<{ programId: string }>();
  const { watch, control } = useFormContext<ProgramService.ProgramModel>();
  const { triggerEvent, programsToTrigger, subjectType } = watch();
  const { hq } = useOfficeAndHq();

  const { data } = useQuery(
    ["available-trigger-events", hq?.id],
    () => getAvailableTriggerEventsV2(hq!.id),
    {
      enabled: Boolean(hq?.id),
      select: data =>
        data.filter(trigger => trigger.subjectType === subjectType)
    }
  );

  const hasAdminEditPermission = useCheckPermission({
    scope: "ProgramAdmin",
    action: "Edit"
  });

  if (!data) {
    return null;
  }

  return (
    <Card>
      <H4>{t("programBuilder.program.details.trigger.header")}</H4>
      <Box pY="m" display="flex" flexDirection="row">
        <Controller
          name="triggerEvent"
          control={control}
          render={({ field }) => (
            <Dropdown
              items={data}
              name="triggerEvent"
              value={
                data.find(x => x.triggerEvent === field.value)?.triggerEventName
              }
              valueObjectIndexAccessor="triggerEventName"
              onChange={item => field.onChange(item.triggerEvent)}
              label={t(
                "programBuilder.program.details.trigger.triggerEvent.label"
              )}
              disabled={disabledFields("triggerEvent")}
            />
          )}
        />

        <When truthy={triggerEvent === "ProgramInstanceCompleted"}>
          <Box pX="gutter">
            <TriggerByPrograms />
          </Box>
        </When>

        {programsToTrigger && programsToTrigger.length > 0 ? (
          <Box pX="gutter">
            <ProgramsToTrigger programsToTrigger={programsToTrigger} />
          </Box>
        ) : null}
      </Box>
      {hasAdminEditPermission && (
        <Box>
          <Link to={`/back-office/hq/${hq!.id}/manually-start/${programId}`}>
            {t("programBuilder.program.details.trigger.link.manually")}
          </Link>
        </Box>
      )}
      <Box>
        <Link to={`/customer/hq/${hq?.id}/triggers`}>
          {t("programBuilder.program.details.trigger.link.custom")}
        </Link>
      </Box>
    </Card>
  );
};
